import { useState, useEffect, useRef, useCallback } from "react";
import { Hash, Users, ArrowDown, Menu, Bell, Wifi, WifiOff, Volume2, VolumeX, Shield } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useChat } from "@/hooks/useChat";
import Sidebar from "@/components/Sidebar";
import MessageBubble from "@/components/MessageBubble";
import MessageInput from "@/components/MessageInput";
import PresenceList from "@/components/PresenceList";
import PinnedBar from "@/components/PinnedBar";
import TypingIndicator from "@/components/TypingIndicator";
import ThemeToggle from "@/components/ThemeToggle";
import { mediaUrl } from "@/lib/chatApi";

export default function ChatLayout({ user, onLogout, chatOptions, CustomSidebar }) {
  const chat = useChat(user, null, null, chatOptions);
  const {
    rooms, groups, activeRoom, messages, pinned, presence, typingUsers, unread, totalUnread,
    connected, muted, setMuted, users, canModerate, moderators, lastByRoom,
    selectRoom, sendMessage, sendTyping, sendReaction, sendPin, addRoom, updateRoom, refreshGroups,
  } = chat;

  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  const feedRef = useRef(null);
  const bottomRef = useRef(null);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior });
  }, []);

  const jumpPending = useRef(true);

  useEffect(() => {
    const el = feedRef.current;
    if (!el || messages.length === 0) return;
    if (jumpPending.current) {
      jumpPending.current = false;
      requestAnimationFrame(() => scrollToBottom("auto"));
      return;
    }
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (nearBottom) scrollToBottom("smooth");
  }, [messages, scrollToBottom]);

  useEffect(() => {
    setReplyTo(null);
    jumpPending.current = true;
  }, [activeRoom?.id]);

  const handleScroll = () => {
    const el = feedRef.current;
    if (!el) return;
    setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 300);
  };

  const handleSend = (text, imageUrl) => {
    const reply = replyTo
      ? { id: replyTo.id, username: replyTo.username, text: replyTo.text || "" }
      : null;
    sendMessage(text, imageUrl, reply);
    setReplyTo(null);
  };

  const roomsWithUnread = rooms.filter((r) => r.id !== activeRoom?.id && unread[r.id] > 0);

  const renderSidebar = () => CustomSidebar ? (
    <CustomSidebar
      rooms={rooms}
      activeRoom={activeRoom}
      unread={unread}
      lastByRoom={lastByRoom}
      onSelect={(r) => { selectRoom(r); setSheetOpen(false); }}
      user={user}
      onLogout={onLogout}
    />
  ) : (
    <Sidebar
      rooms={rooms}
      groups={groups}
      moderators={moderators}
      activeRoom={activeRoom}
      unread={unread}
      onSelect={(r) => { selectRoom(r); setSheetOpen(false); }}
      user={user}
      onLogout={onLogout}
      onRoomCreated={(room) => { addRoom(room); selectRoom(room); }}
      onRoomUpdated={(room) => updateRoom(room)}
      refreshGroups={refreshGroups}
    />
  );

  return (
    <div className="h-screen w-full flex bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-50 overflow-hidden font-sans">
      <div className="hidden md:flex">{renderSidebar()}</div>

      <div className="flex-1 flex flex-col h-full relative bg-slate-50 dark:bg-slate-950/80 overflow-hidden">
        <div className="h-16 border-b border-slate-200 dark:border-slate-800/80 px-4 md:px-6 flex items-center justify-between bg-white/80 dark:bg-slate-900/70 backdrop-blur-md shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className="md:hidden p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-80 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                {renderSidebar()}
              </SheetContent>
            </Sheet>

            {activeRoom?.logo ? (
              <img
                src={mediaUrl(activeRoom.logo)}
                alt={activeRoom.name}
                data-testid="chat-room-logo"
                className="w-8 h-8 rounded-lg object-cover shrink-0 border border-slate-200 dark:border-slate-700"
              />
            ) : (
              <Hash className="w-5 h-5 text-indigo-500 dark:text-indigo-400 shrink-0" />
            )}
            <div className="min-w-0">
              <h2 data-testid="chat-room-title" className="text-base font-semibold tracking-wide truncate flex items-center gap-2">
                {activeRoom?.name || "Memuat..."}
                {canModerate && (
                  <span data-testid="moderator-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
                    <Shield className="w-3 h-3" /> Moderator
                  </span>
                )}
              </h2>
              {activeRoom?.description && <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{activeRoom.description}</p>}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <span
              data-testid="connection-status"
              className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                connected
                  ? "bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
                  : "bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20 animate-pulse"
              }`}
            >
              {connected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
              {connected ? "Tersambung" : "Menyambung ulang..."}
            </span>

            <ThemeToggle />

            <button
              data-testid="mute-toggle"
              onClick={() => setMuted((m) => !m)}
              title={muted ? "Nyalakan suara notifikasi" : "Bisukan suara notifikasi"}
              className="p-2 rounded-lg text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>

            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="notifications-bell" className="relative p-2 rounded-lg text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
                  <Bell className="w-5 h-5" />
                  {totalUnread > 0 && (
                    <span data-testid="total-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-indigo-500 text-white text-[9px] font-bold flex items-center justify-center">
                      {totalUnread > 99 ? "99+" : totalUnread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="end" className="w-72 p-3 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide px-1 mb-2">Pesan belum dibaca</p>
                {roomsWithUnread.length === 0 && <p className="text-xs text-slate-400 dark:text-slate-500 px-1 py-2">Semua sudah terbaca 🎉</p>}
                {roomsWithUnread.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`notif-room-${r.id}`}
                    onClick={() => selectRoom(r)}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left"
                  >
                    <Hash className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                    <span className="text-sm text-slate-700 dark:text-slate-200 flex-1 truncate">{r.name}</span>
                    <span className="min-w-5 h-5 px-1.5 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {unread[r.id]}
                    </span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="active-users-toggle"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 hover:bg-emerald-200 dark:hover:bg-emerald-500/20 transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                  {presence.length} online
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="end" className="p-0 border-0 bg-transparent w-auto">
                <PresenceList users={presence} />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <PinnedBar pinned={pinned} onUnpin={(id) => sendPin(id, false)} canModerate={canModerate} />

        <div
          ref={feedRef}
          onScroll={handleScroll}
          data-testid="chat-messages-container"
          className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4"
        >
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 dark:text-slate-500 gap-2">
              <Hash className="w-10 h-10 opacity-40" />
              <p className="text-sm">Belum ada pesan. Mulai obrolan sekarang!</p>
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              mine={m.username === user.username}
              currentUser={user.username}
              onReply={setReplyTo}
              onReact={sendReaction}
              onPin={canModerate ? (msg) => sendPin(msg.id, !msg.pinned) : undefined}
            />
          ))}
          <div ref={bottomRef} />
        </div>

        {showScrollBtn && (
          <button
            data-testid="scroll-to-bottom-button"
            onClick={() => scrollToBottom("smooth")}
            className="absolute bottom-28 right-6 w-10 h-10 rounded-full bg-indigo-600 hover:bg-indigo-500 flex items-center justify-center shadow-lg z-30"
          >
            <ArrowDown className="w-5 h-5 text-white" />
          </button>
        )}

        <TypingIndicator users={typingUsers} />

        {activeRoom && (
          <MessageInput
            onSend={handleSend}
            onTyping={sendTyping}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            users={users}
          />
        )}
      </div>
    </div>
  );
}
